/**
 * This hook provides the defaults for the workout profile by workout type from Wahoo Cloud.
 */
import { WorkoutTypes } from "@WahooFitness/cloud-client-ts";
import { WahooWorkoutType, WorkoutTypeType } from "@WahooFitness/cloud-client-types";
import {
  getStoredValue,
  setStoredValue,
  useCloudContext,
  useConfigContext,
} from "@WahooFitness/wahoo-offline-mfe";
import { useMemo } from "react";
import useSWRImmutable from "swr/immutable";

type CachedWorkoutTypes = {
  workoutTypes: WorkoutTypeType[];
  updatedAt: number;
};

const THIRTY_DAYS_IN_MS = 30 * 24 * 60 * 60 * 1000;
const CACHE_KEY = "cache:/default-workout-types";

export const useDefaultWorkoutTypes = () => {
  const { wahooToken } = useConfigContext();
  const { getCloudClient } = useCloudContext();
  const workoutTypeClient = getCloudClient(WorkoutTypes);

  const { data: workoutTypes, isLoading: defaultWorkoutTypesAreLoading } = useSWRImmutable(
    ["/default-workout-types"],
    async () => {
      const cachedWorkoutTypes = await getStoredValue<CachedWorkoutTypes>(CACHE_KEY);
      if (cachedWorkoutTypes && Date.now() - cachedWorkoutTypes.updatedAt < THIRTY_DAYS_IN_MS) {
        return cachedWorkoutTypes.workoutTypes;
      }
      try {
        const cloudWorkoutTypes = await workoutTypeClient.getWorkoutTypes(wahooToken);
        if (cloudWorkoutTypes.length > 0) {
          await setStoredValue(CACHE_KEY, {
            workoutTypes: cloudWorkoutTypes,
            updatedAt: Date.now(),
          });
        }
        return cloudWorkoutTypes;
      } catch (e) {
        console.error("Settings failed to get the default workout types from Cloud");
        // If we couldn't get the default types over the network, try to return
        // a previously cached copy.
        if (cachedWorkoutTypes?.workoutTypes) {
          return cachedWorkoutTypes.workoutTypes;
        }
        throw e;
      }
    }
  );

  const defaultWorkoutTypesMap = useMemo(() => {
    return workoutTypes?.reduce(
      (acc, workoutType) => {
        const {
          id,
          name: _name,
          updated_at: _updatedAt,
          created_at: _createdAt,
          ...rest
        } = workoutType;
        acc[id as WahooWorkoutType] = rest;
        return acc;
      },
      {} as Record<WahooWorkoutType, Partial<WorkoutTypeType>>
    );
  }, [workoutTypes]);

  return { defaultWorkoutTypesMap, defaultWorkoutTypesAreLoading };
};
